/**
 * Quick Add Supplier Dialog
 *
 * Adds a supplier from inside the PO editor (a PO for a new factory should not
 * send someone to the Cashflow page first). Name plus payment terms split by
 * percentage across a deposit before the order, a payment on docs (the ETD),
 * and a payment X days after ETD — the three must add up to 100%. The full
 * supplier manager on the Cashflow page can refine them later.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CashflowService from "services/CashflowService";
import { PaymentTranche, Supplier } from "models/CashflowModel";

interface QuickAddSupplierDialogProps {
  open: boolean;
  onClose: () => void;
  existing: Supplier[];
  /** The refreshed supplier list and the one just created */
  onCreated: (suppliers: Supplier[], created: Supplier) => void;
}

const pct = (value: string) => {
  const n = parseFloat(value);
  return isNaN(n) ? 0 : n;
};

export default function QuickAddSupplierDialog({ open, onClose, existing, onCreated }: QuickAddSupplierDialogProps): JSX.Element {
  const service = useMemo(() => new CashflowService(), []);
  const [name, setName] = useState("");
  // Percentages as typed; only the non-zero ones become tranches
  const [depositPercent, setDepositPercent] = useState("");
  const [docsPercent, setDocsPercent] = useState("");
  const [afterEtdPercent, setAfterEtdPercent] = useState("");
  const [daysAfterEtd, setDaysAfterEtd] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(""); setDepositPercent(""); setDocsPercent(""); setAfterEtdPercent(""); setDaysAfterEtd(""); setErrorMessage(null);
  }, [open]);

  const duplicate = existing.find(s => s.name.trim().toLowerCase() === name.trim().toLowerCase());
  const total = Math.round((pct(depositPercent) + pct(docsPercent) + pct(afterEtdPercent)) * 100) / 100;
  const totalOk = Math.abs(total - 100) < 0.005;
  const anyNegative = [depositPercent, docsPercent, afterEtdPercent].some(v => pct(v) < 0);
  const daysNeeded = pct(afterEtdPercent) > 0;
  const daysOk = !daysNeeded || (daysAfterEtd !== "" && parseInt(daysAfterEtd, 10) >= 0);
  const canSave = name.trim().length > 0 && !duplicate && totalOk && !anyNegative && daysOk;

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const tranches: PaymentTranche[] = [];
      if (pct(depositPercent) > 0) tranches.push({ trancheOrder: tranches.length + 1, triggerType: "upon_placement", percentage: pct(depositPercent) / 100, daysAfterEtd: null });
      if (pct(docsPercent) > 0) tranches.push({ trancheOrder: tranches.length + 1, triggerType: "on_etd", percentage: pct(docsPercent) / 100, daysAfterEtd: null });
      if (pct(afterEtdPercent) > 0) tranches.push({ trancheOrder: tranches.length + 1, triggerType: "x_days_after_etd", percentage: pct(afterEtdPercent) / 100, daysAfterEtd: parseInt(daysAfterEtd, 10) });
      const result = await service.createSupplier({ name: name.trim(), maxOrderAmount: null, paymentTerms: tranches });
      if (!result) throw new Error("The supplier was not created");
      const created = result.suppliers.find(s => s.name.trim().toLowerCase() === name.trim().toLowerCase());
      if (!created) throw new Error("The supplier was created but did not come back in the list");
      onCreated(result.suppliers, created);
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const percentField = (label: string, value: string, setValue: (v: string) => void, helper?: string) => (
    <TextField
      size="small"
      label={label}
      type="number"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      helperText={helper}
      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
      inputProps={{ min: 0, max: 100, step: 0.5 }}
      sx={{ width: 170 }}
    />
  );

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a supplier</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {errorMessage && <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>}
        <TextField
          size="small"
          label="Supplier name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
          error={!!duplicate}
          helperText={duplicate ? `${duplicate.name} already exists — pick it from the list` : "The factory or agent the PO is placed with, e.g. Platform"}
        />
        <Typography variant="subtitle2">Payment terms — split the PO amount across these (must add up to 100%)</Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          {percentField("Deposit (before order)", depositPercent, setDepositPercent, "Due when the PO is placed")}
          {percentField("On docs", docsPercent, setDocsPercent, "Due when shipping documents issue (the ETD)")}
          {percentField("After ETD", afterEtdPercent, setAfterEtdPercent, "Due X days after ETD")}
          <TextField
            size="small"
            label="X days after ETD"
            type="number"
            value={daysAfterEtd}
            onChange={(e) => setDaysAfterEtd(e.target.value)}
            disabled={!daysNeeded}
            error={daysNeeded && !daysOk}
            helperText={daysNeeded ? (daysOk ? "e.g. 55" : "Enter the number of days") : "Only when a % is due after ETD"}
            inputProps={{ min: 0 }}
            sx={{ width: 150 }}
          />
        </Box>
        <Typography variant="body2" color={totalOk ? "success.main" : "error.main"}>
          Total: {total}%{totalOk ? "" : ` — must equal 100% (${total < 100 ? `${Math.round((100 - total) * 100) / 100}% short` : `${Math.round((total - 100) * 100) / 100}% over`})`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          A max order amount, more tranches, and the vendor block printed on PO PDFs are set in Cashflow → Suppliers.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving}>{isSaving ? "Adding…" : "Add supplier"}</Button>
      </DialogActions>
    </Dialog>
  );
}
